import { Button } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { canReviewComponents } from "../lib/can-review-components"
import { useCameFromDrafts } from "../lib/use-came-from-drafts"
import { VIEW_DRAFTS } from "../lib/library-views"

/**
 * The task toolbar's return button: back to the Draft Components list when the component
 * was opened from there, otherwise back to the folder listing.
 *
 * Both destinations are rendered by this one component on purpose. ButtonGroup assigns its
 * first/middle/last corner rounding from `React.Children.toArray`, which counts elements
 * rather than what they render — so two sibling components, one returning null, would still
 * count as two children and leave the visible one styled as a middle button with a square,
 * borderless right edge.
 *
 * `size` and `sx` are forwarded for the standalone usage. Inside a ButtonGroup both come from
 * the group, so callers there pass neither.
 *
 * @param {object} props
 * @param {string} [props.size] MUI Button size, when rendered outside a ButtonGroup
 * @param {object} [props.sx] MUI sx overrides, when rendered outside a ButtonGroup
 */
export function BackToListButton({ size, sx }) {
    const { setSelectedTaskId, onSelectView } = useBoundContext()
    const returnsToDrafts = useCameFromDrafts() && canReviewComponents()

    return (
        <Button
            data-cy={getDataCy()}
            variant="outlined"
            color="primary"
            size={size}
            onClick={handleClick}
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            sx={sx}
        >
            {getLabel()}
        </Button>
    )

    function getDataCy() {
        if (returnsToDrafts) return "back-to-drafts-button"
        return "back-to-list-button"
    }

    function getLabel() {
        if (returnsToDrafts) return "Back to Drafts"
        return "Back to List"
    }

    function handleClick() {
        if (returnsToDrafts) {
            onSelectView(VIEW_DRAFTS)
            return
        }
        setSelectedTaskId(null)
    }
}
